import React, { lazy } from 'react';
import { AdminRoutes, AuthRoutes, NavElements, PublicRoutes, RouteNames } from './enums/RouteEnums';
import DashboardOverview from './components/Dashboard/Dashboard';
import Calendar from './components/Calendar/CalendarList';
import Login from './components/Login';
import Products from './components/Products/ProductList';
import MembershipInfoWrapper from './components/Memberships/MembershipInfoWrapper';
import ProductInfoWrapper from './components/Products/ProductInfoWrapper';

// Lazy load MeetingList to avoid circular dependency with AppContext
const MeetingList = lazy(() => import('./components/Meetings/MeetingList'));
// Lazy load ContactList
const Contacts = lazy(() => import('./components/Contacts/ContactList'));
// Lazy load MembershipList
const Memberships = lazy(() => import('./components/Memberships/MembershipList'));
// Lazy load CompanyList
const Companies = lazy(() => import('./components/Companies/CompanyList'));
// Lazy load InvoiceList
const Invoices = lazy(() => import('./components/Invoices/InvoiceList'));
// Lazy load DocumentsList
const Documents = lazy(() => import('./components/Documents/DocumentsList'));
import IndexInfoWrapper from './components/Indices/IndexInfoWrapper';
import Stocks from './components/Stocks/StocksList';
import Event from './components/Cvent/CventList';
import {
  SvgIconComponent,
  Dashboard,
  AssignmentInd,
  ImportContacts,
  HomeWork,
  DateRange,
  Receipt,
  Mail,
  ShowChart,
  FormatListBulleted,
  Autorenew,
  InsertChart,
  AdminPanelSettings,
  Person,
  People,
  Construction,
  EmojiSymbols,
  LocalMall,
  Settings,
  Web,
  Description,
  Summarize
} from '@mui/icons-material';
import EventIcon from '@mui/icons-material/Event';
import { Roles } from './enums/RoleEnums';
import FtseConfigWrapper from './components/Ftse/FtseConfigWrapper';
import SiteSectionList from './components/Admin/SiteSections/SiteSectionList';
import MeetingTypeList from './components/Admin/MeetingTypes/MeetingTypeList';
import Users from './components/Admin/Users/UsersList';
import UserDetails from './components/Admin/Users/UserInfoWrapper';
import UserGroups from './components/Admin/UserGroups';
import Customatization from './components/Admin/Customatization';
import Interests from './components/Admin/Interests';
import ContactDetails from './components/Contacts/ContactInfoWrapper';
import CompanyDetails from './components/Companies/CompanyInfoWrapper';
import MassMail from './components/MassMail/MassMailInfoWrapper';
import StockDetails from './components/Stocks/StockInfoWrapper';
import GenerateCache from './components/Msb/GenerateCache';
import FtseInfoWrapper from './components/Ftse/FtseInfoWrapper';
import EtfsInfoWrapper from './components/Etfs/EtfsInfoWrapper';
import EtfHistory from './components/Etfs/EtfHistory';

export interface IRouteModel {
  id?: number;
  name: string;
  path?: string;
  icon?: SvgIconComponent;
  isPublic?: boolean;
  exact?: boolean;
  Component?: React.FunctionComponent | React.LazyExoticComponent<React.FunctionComponent>;
  requiredRole?: string | null;
  showInNav: boolean;
  subRoutes?: IRouteModel[];
  type: NavElements;
  svgIconName?: string;
}

export const Routes: IRouteModel[] = [
  {
    name: RouteNames.Home,
    path: AuthRoutes.Home,
    icon: Dashboard,
    isPublic: false,
    Component: DashboardOverview,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Memberships,
    path: AuthRoutes.Memberships,
    icon: AssignmentInd,
    isPublic: false,
    Component: Memberships,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.MembershipDetails,
    path: AuthRoutes.MembershipDetails,
    isPublic: false,
    Component: MembershipInfoWrapper,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.Login,
    path: PublicRoutes.Login,
    isPublic: true,
    exact: false,
    Component: Login,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.EtfValues,
    path: AuthRoutes.EtfValues,
    isPublic: false,
    Component: EtfHistory,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.CompanyDetails,
    path: AuthRoutes.CompanyDetails,
    isPublic: false,
    Component: CompanyDetails,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.Companies,
    path: AuthRoutes.Companies,
    icon: HomeWork,
    isPublic: false,
    Component: Companies,
    exact: false,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Contacts,
    path: AuthRoutes.Contacts,
    icon: ImportContacts,
    isPublic: false,
    Component: Contacts,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.ContactDetails,
    path: AuthRoutes.ContactDetails,
    isPublic: false,
    Component: ContactDetails,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.Event,
    path: AuthRoutes.Event,
    icon: EventIcon,
    isPublic: false,
    Component: Event,
    exact: false,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Calendar,
    path: AuthRoutes.Calendar,
    icon: DateRange,
    isPublic: false,
    Component: Calendar,
    exact: false,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Invoices,
    path: AuthRoutes.Invoices,
    icon: Receipt,
    isPublic: false,
    Component: Invoices,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Products,
    path: AuthRoutes.Products,
    icon: LocalMall,
    isPublic: false,
    Component: Products,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.ProductDetails,
    path: AuthRoutes.ProductDetails,
    isPublic: false,
    Component: ProductInfoWrapper,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.Meetings,
    path: AuthRoutes.Meetings,
    icon: EventIcon,
    isPublic: false,
    Component: MeetingList,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.MassMail,
    path: AuthRoutes.MassMail,
    icon: Mail,
    isPublic: false,
    Component: MassMail,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.AdminUserDetails,
    showInNav: false,
    Component: UserDetails,
    exact: true,
    isPublic: false,
    path: AdminRoutes.UserDetails,
    requiredRole: Roles.Admin,
    type: NavElements.Item
  },
  {
    name: RouteNames.Stocks,
    path: AuthRoutes.Stocks,
    showInNav: true,
    Component: Stocks,
    exact: true,
    icon: ShowChart,
    isPublic: false,
    // requiredRole: Roles.Admin,
    type: NavElements.Item,
    svgIconName: 'stockSvg'
  },
  {
    name: RouteNames.StockDetails,
    path: AuthRoutes.StockDetails,
    isPublic: false,
    Component: StockDetails,
    exact: false,
    showInNav: false,
    type: NavElements.Item
  },
  {
    name: RouteNames.Indices,
    path: AuthRoutes.Indices,
    icon: FormatListBulleted,
    isPublic: false,
    Component: IndexInfoWrapper,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Etfs,
    path: AuthRoutes.Etfs,
    icon: Autorenew,
    isPublic: false,
    Component: EtfsInfoWrapper,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Ftse,
    path: AuthRoutes.Ftse,
    icon: InsertChart,
    isPublic: false,
    Component: FtseInfoWrapper,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Documents,
    path: AuthRoutes.Documents,
    icon: Description,
    isPublic: false,
    Component: Documents,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Msb,
    path: AuthRoutes.Msb,
    icon: Summarize,
    isPublic: false,
    Component: GenerateCache,
    exact: true,
    showInNav: true,
    type: NavElements.Item
  },
  {
    name: RouteNames.Admin,
    showInNav: true,
    icon: AdminPanelSettings,
    requiredRole: Roles.Admin,
    type: NavElements.Group,
    subRoutes: [
      {
        name: RouteNames.AdminUsers,
        showInNav: true,
        Component: Users,
        exact: true,
        icon: Person,
        isPublic: false,
        path: AdminRoutes.Users,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminUserGroups,
        showInNav: true,
        Component: UserGroups,
        exact: true,
        icon: People,
        isPublic: false,
        path: AdminRoutes.UserGroups,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminCustomization,
        showInNav: true,
        Component: Customatization,
        exact: true,
        icon: Construction,
        isPublic: false,
        path: AdminRoutes.Customization,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminInterests,
        showInNav: true,
        Component: Interests,
        exact: true,
        icon: EmojiSymbols,
        isPublic: false,
        path: AdminRoutes.Interests,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminFtseConfig,
        showInNav: true,
        Component: FtseConfigWrapper,
        exact: true,
        icon: Settings,
        isPublic: false,
        path: AdminRoutes.FTSEConfig,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminSiteSections,
        showInNav: true,
        Component: SiteSectionList,
        exact: true,
        icon: Web,
        isPublic: false,
        path: AdminRoutes.SiteSections,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      },
      {
        name: RouteNames.AdminMeetingTypes,
        showInNav: true,
        Component: MeetingTypeList,
        exact: true,
        icon: EventIcon,
        isPublic: false,
        path: AdminRoutes.MeetingTypes,
        requiredRole: Roles.Admin,
        type: NavElements.Item
      }
    ]
  }
];

export const getRoutes = (): IRouteModel[] => {
  let index = 0;
  const result: IRouteModel[] = [];
  Routes.forEach((r: IRouteModel) => {
    if (r.type === NavElements.Item) {
      result.push({ id: index++, ...r });
    } else {
      const sRoutes = getSubroutes(r, index);
      result.push({ ...r, id: index, subRoutes: sRoutes });
      index = r.subRoutes ? index + r.subRoutes.length + 1 : index + 1;
    }
  });
  return result;
};

const getSubroutes = (route: IRouteModel, index: number) => {
  const result: IRouteModel[] = [];
  route.subRoutes?.forEach((sr) => {
    result.push({ id: ++index, ...sr });
  });
  return result;
};
